import { DROP_OUTCOMES, DROP_SORTS, RETAILER_LABELS, RETAILERS, type DropSort, type DropStats } from '@nebueye/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowUpDown } from 'lucide-react'
import { z } from 'zod'
import { Meter } from '@/components/app/outcome-bar'
import { ProductThumb, RetailerMark } from '@/components/app/retailer'
import { SearchBox } from '@/components/app/search-box'
import { EmptyState, ErrorState, OrbitArt } from '@/components/app/states'
import {
  ColumnHeader,
  GroupLabel,
  PageTitle,
  Pager,
  Pips,
  RetailerTabs,
  RowChevron,
  StatBar,
  Surface,
  WentThroughLead,
  rowClass,
  rowEnter,
} from '@/components/app/surface'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { ago, count, percent, plural } from '@/lib/format'
import { dropsQuery } from '@/lib/queries'
import { cn } from '@/lib/utils'

const PAGE = 30

const SORT_LABELS: Record<DropSort, string> = {
  recent: 'Newest first',
  orders: 'Most orders',
  best: 'Best success rate',
  worst: 'Worst success rate',
}

export const Route = createFileRoute('/_app/drops/')({
  validateSearch: z.object({
    retailer: z.enum(RETAILERS).optional(),
    outcome: z.enum(DROP_OUTCOMES).optional(),
    sort: z.enum(DROP_SORTS).optional(),
    search: z.string().optional(),
    page: z.number().int().min(1).optional(),
  }),
  head: () => ({ meta: [{ title: 'Drops · Nebueye' }] }),
  component: Drops,
})

/** Row columns from `lg` up: product · orders · rate · fulfilment · chevron. */
const COLS = 'grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1fr)_17rem_5.5rem_15rem_1.25rem]'

function Drops() {
  const s = Route.useSearch()
  const navigate = Route.useNavigate()
  const page = s.page ?? 1
  const sort = s.sort ?? 'recent'
  const q = useQuery(
    dropsQuery({ retailer: s.retailer, outcome: s.outcome, search: s.search, sort, offset: (page - 1) * PAGE, limit: PAGE }),
  )
  const set = (patch: Partial<typeof s>) => navigate({ search: (p) => ({ ...p, page: undefined, ...patch }), replace: true })
  const filtered = !!(s.retailer || s.outcome || s.search)
  const data = q.data
  const sum = data?.summary

  return (
    <div className="space-y-6">
      <PageTitle
        title="Drops"
        description="Every release you went for, and how many orders made it."
        action={
          <SearchBox
            value={s.search ?? ''}
            onChange={(v) => set({ search: v || undefined })}
            placeholder="Search products"
            label="Search drops by product"
            shortcut
            className="w-full sm:w-72"
          />
        }
      />

      {sum ? (
        <StatBar
          lead={<WentThroughLead kept={sum.kept} total={sum.orders} />}
          stats={[
            { label: 'Drops', value: count(sum.drops), sub: `${plural(sum.orders, 'order')} placed` },
            { label: 'Cancelled', value: count(sum.cancelled), sub: sum.orders ? `${percent(sum.cancelled / sum.orders)} of orders` : 'None yet' },
            { label: 'Shipped', value: count(sum.shipped), sub: sum.kept ? `${percent(sum.shipped / sum.kept)} of what went through` : 'Nothing yet' },
            { label: 'Delivered', value: count(sum.delivered), sub: sum.kept ? `${percent(sum.delivered / sum.kept)} of what went through` : 'Nothing yet' },
          ]}
        />
      ) : (
        !q.isError && <Skeleton className="h-[104px] w-full rounded-xl" />
      )}

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <RetailerTabs value={s.retailer} counts={data?.retailerCounts} onChange={(r) => set({ retailer: r })} />
        <div className="flex items-center gap-2">
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={s.outcome ?? 'all'}
            onValueChange={(v) => v && set({ outcome: v === 'all' ? undefined : (v as typeof s.outcome) })}
            aria-label="Outcome"
            className="bg-card"
          >
            <ToggleGroupItem value="all" className="px-3">All</ToggleGroupItem>
            <ToggleGroupItem value="success" className="px-3">Wins</ToggleGroupItem>
            <ToggleGroupItem value="cancelled" className="px-3">Cancels</ToggleGroupItem>
          </ToggleGroup>
          <Select value={sort} onValueChange={(v) => set({ sort: v === 'recent' ? undefined : (v as DropSort) })}>
            <SelectTrigger size="sm" className="bg-card" aria-label="Sort drops">
              <span className="flex items-center gap-2">
                <ArrowUpDown className="text-subtle size-3.5" />
                <SelectValue />
              </span>
            </SelectTrigger>
            <SelectContent align="end">
              {DROP_SORTS.map((k) => (
                <SelectItem key={k} value={k}>
                  {SORT_LABELS[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Surface aria-label="Drops">
        <ColumnHeader cols={COLS}>
          <span>Product</span>
          <span>Orders</span>
          <span className="text-right">Success</span>
          <span>Fulfilment</span>
          <span />
        </ColumnHeader>

        {q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} className="px-6" />
        ) : !data ? (
          <RowsSkeleton />
        ) : data.drops.length === 0 ? (
          <EmptyState
            className="px-6"
            art={<OrbitArt />}
            title={filtered ? 'No drops match' : 'No drops yet'}
            action={
              filtered && (
                <Button variant="outline" onClick={() => navigate({ search: {}, replace: true })}>
                  Clear filters
                </Button>
              )
            }
          >
            {filtered
              ? 'Try a different product name, retailer or outcome.'
              : 'When several orders for one product land within an hour, they are grouped into a drop so you can see how it went.'}
          </EmptyState>
        ) : (
          <ul className={cn('transition-opacity duration-150', q.isPlaceholderData && 'opacity-50')}>
            {data.drops.map((d, i) => {
              const group = sort === 'recent' ? dayGroup(d.dropStartedAt) : null
              const prev = data.drops[i - 1]
              const showGroup = group && (!prev || dayGroup(prev.dropStartedAt) !== group)
              return (
                <li key={d.id} className="border-b last:border-b-0">
                  {showGroup && <GroupLabel>{group}</GroupLabel>}
                  <DropRow d={d} index={i} flagNew={!group} />
                </li>
              )
            })}
          </ul>
        )}

        {data && <Pager page={page} pageSize={PAGE} total={data.total} noun="drop" onPage={(p) => set({ page: p === 1 ? undefined : p })} />}
      </Surface>
    </div>
  )
}

const DAY = 86_400_000
function dayGroup(iso: string) {
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  const t = Date.parse(iso)
  if (Date.now() - t < DAY) return 'Last 24 hours'
  if (t >= start.getTime() - 6 * DAY) return 'This week'
  if (t >= start.getTime() - 29 * DAY) return 'This month'
  return 'Earlier'
}

function DropRow({ d, index, flagNew }: { d: DropStats; index: number; flagNew: boolean }) {
  const rate = d.totalOrders ? d.confirmedOrders / d.totalOrders : 0
  // Under "Newest first" the group heading already says it's recent.
  const fresh = flagNew && Date.now() - Date.parse(d.dropStartedAt) < DAY
  const enter = rowEnter(index)
  return (
    <Link to="/drops/$id" params={{ id: d.id }} style={enter.style} className={cn(rowClass, enter.className, COLS)}>
      <div className="flex min-w-0 items-center gap-3.5">
        <ProductThumb src={d.imageUrl} retailer={d.retailer} className="size-11 rounded-lg text-sm" />
        <div className="min-w-0 space-y-0.5">
          <p className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{d.productName}</span>
            {fresh && (
              <span className="bg-accent text-accent-foreground shrink-0 rounded-full px-1.5 py-px text-[10px] font-semibold tracking-wide uppercase">
                New
              </span>
            )}
          </p>
          <p className="text-muted-foreground flex items-center gap-1.5 text-[13px]">
            <RetailerMark retailer={d.retailer} className="size-4 rounded text-[7px]" />
            <span className="truncate">
              {RETAILER_LABELS[d.retailer]} <span className="text-subtle">· {ago(d.dropStartedAt)}</span>
            </span>
          </p>
        </div>
      </div>

      <div className="order-3 col-span-2 flex items-center gap-3 lg:order-none lg:col-span-1">
        <p className="tabular w-12 shrink-0 text-sm">
          <span className="font-semibold">{count(d.confirmedOrders)}</span>
          <span className="text-subtle">/{count(d.totalOrders)}</span>
        </p>
        <Pips kept={d.confirmedOrders} cancelled={d.cancelledOrders} />
      </div>

      <p
        className={cn(
          'tabular text-right text-[15px] font-semibold',
          rate === 1 ? 'text-status-success-text' : rate < 0.5 ? 'text-status-danger-text' : 'text-foreground',
        )}
      >
        {percent(rate)}
      </p>

      <Fulfilment d={d} />
      <RowChevron />
    </Link>
  )
}

function Fulfilment({ d }: { d: DropStats }) {
  const kept = d.confirmedOrders
  const state =
    kept === 0
      ? { label: 'All cancelled', dot: 'bg-status-danger', detail: 'Nothing to ship' }
      : d.deliveredOrders >= kept
        ? { label: 'Delivered', dot: 'bg-status-success', detail: `All ${count(kept)} arrived` }
        : d.shippedOrders > 0
          ? {
              label: 'Shipping',
              dot: 'bg-status-progress',
              detail: `${count(d.shippedOrders)} of ${count(kept)} shipped${d.deliveredOrders ? ` · ${count(d.deliveredOrders)} delivered` : ''}`,
            }
          : { label: 'Waiting to ship', dot: 'bg-status-neutral', detail: plural(kept, 'order') }
  const inTransit = Math.max(0, Math.min(kept, d.shippedOrders) - d.deliveredOrders)
  return (
    <div className="order-4 col-span-2 space-y-1.5 lg:order-none lg:col-span-1">
      <p className="flex items-center gap-2 text-[13px]">
        <span className={cn('size-1.5 shrink-0 rounded-full', state.dot)} aria-hidden />
        <span className="font-medium">{state.label}</span>
        <span className="text-subtle tabular truncate">{state.detail}</span>
      </p>
      {kept > 0 && (
        <Meter
          label={`${Math.min(kept, d.deliveredOrders)} delivered, ${inTransit} shipping, of ${kept}`}
          className="h-1"
          segments={[
            { value: Math.min(kept, d.deliveredOrders), className: 'bg-status-success' },
            { value: inTransit, className: 'bg-status-progress' },
            { value: Math.max(0, kept - Math.min(kept, d.deliveredOrders) - inTransit), className: 'bg-transparent' },
          ]}
        />
      )}
    </div>
  )
}

function RowsSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading drops">
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className={cn('grid items-center gap-x-6 gap-y-3 border-b px-5 py-3.5 last:border-b-0', COLS)}>
          <div className="flex items-center gap-3.5">
            <Skeleton className="size-11 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3.5 w-3/5 max-w-72" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
          <Skeleton className="hidden h-3 w-40 lg:block" />
          <Skeleton className="h-4 w-10 justify-self-end" />
          <Skeleton className="hidden h-3 w-44 lg:block" />
          <span />
        </div>
      ))}
    </div>
  )
}
